import { Component, OnInit, ViewChild, OnDestroy, ElementRef} from '@angular/core';
import { BroadcasterEvent } from 'src/app/models/broadcaster-events.enum';
import { ChatService } from 'src/app/services/chat-service/chat.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map, delay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ChatBroadcaster } from 'src/app/services/chat-broadcaster/chat-broadcaster';
import { UserService } from 'src/app/services/user-service/user.service';
import { ChatMsgType } from 'src/app/models/chat-types.enum';
import { ChatMessagesWithDate, DateWiseChatMessages, ChatMessagesHasView } from 'src/app/models/chat-messages';
import { TranslateService } from '@ngx-translate/core';
import { TranslatableError } from '@cordic/request';
import { ChatCount } from 'src/app/models/chat-count.enum';
import { Conversation, DriverDetails, SystemVerbs } from '@cordic/chatmail-web';
import { PredefinedMessage, PredefinedMessages } from 'src/app/models/predefined-messages';

@Component({
  selector: 'app-message-view',
  templateUrl: './message-view.component.html',
  styleUrls: ['./message-view.component.scss']
})
export class MessageViewComponent implements OnInit, OnDestroy {

  @ViewChild('scroll', { static: false }) content: ElementRef;

  ChatMsgType = ChatMsgType;

  private chatMessageSubscription: Subscription;
  message = new PredefinedMessage();
  selectedPredefinedMsg = new PredefinedMessage();
  chatMessages = [];
  newChatMessages: ChatMessagesWithDate[];
  chatMessagesDateWise: DateWiseChatMessages[];
  driverID: number;
  selectedContact: Conversation | DriverDetails;
  scrollToLastMessageInterval;

  oldMessages = [];
  showOldMessages = false;
  showOldMessagesSpinner = false;

  showNoOlderMessages = false;
  showReplyOptions = false;
  showPredefinedMessages = false;
  isLoading = false;
  hasScrolled = false;
  shouldShowNewMsgButton = false;

  predefinedMessages: PredefinedMessage[];
  sentMsgLastChatID = new BehaviorSubject<void>(undefined);

  constructor(
      private chatService: ChatService,
      private userService: UserService,
      private broadcaster: ChatBroadcaster,
      private translateService: TranslateService) { }

  startTimer()
  {
    this.scrollToLastMessageInterval = setInterval(async () =>
    {
      if (!this.hasScrolled)
      {
        const chatInnerLeft = document.getElementsByClassName('chat-inner-left')[0];
        const chatInnerRight = document.getElementsByClassName('chat-inner-right')[0];
    
        if (chatInnerLeft || chatInnerRight) {
          await this.scrollToLastMessage();
        }
      }
    }, 100);
  }

  async scrollToLastMessage() {
    if (this.content)
    {
      // sometimes the angular change detector behaves oddly when picking up changes
      // to the native DOM and, as a workaround, it is possible to escape the change outside the regular 
      // angular event loop with a timeout.
      return new Promise<void>(r => setTimeout(r, 1000))
      .then(() =>
      {
        this.content.nativeElement.scrollTop = this.content.nativeElement.scrollHeight;
        clearInterval(this.scrollToLastMessageInterval);
        this.hasScrolled = true;
      });
    }
  }

  ngOnInit()
  {
    this.predefinedMessages = this.chatService.predefinedMessages.messages;

    this.broadcaster.on(BroadcasterEvent.EVENT_ON_CONTACT_SELECTED)
      .subscribe((contact: Conversation | DriverDetails) =>
      {
        this.isLoading = true;
        return new Promise((resolve, reject) =>
        {
          if ((this.chatMessages) &&
              (this.chatMessageSubscription))
          {
            this.ngOnDestroy();
          }

          this.selectedContact = contact;

          if (contact instanceof DriverDetails)
          {
            this.driverID = contact.id;
          }
          else
          {
            this.driverID = contact.driverID;
          }

          resolve();
        })
        .then(() =>
        {
          this.setViewMessages();
        });
    });

    // If a new message is received, make sure we reset the reachBottom variable
    // in order to show the "no msg button" if we are not already in the bottom of the page
    this.broadcaster.on(BroadcasterEvent.EVENT_ON_MESSAGE_SENT_OR_RETRIEVED)
    .subscribe(async (data: {message: ChatMessagesWithDate,
                             toDriver: Conversation | DriverDetails}) =>
    {
      const isLastMsgCached = data.message ? this.chatMessages.find(msg => msg.chatID === data.message.chatID) : true;

      if (!this.onChatScroll())
      {
        if (!isLastMsgCached)
        {
          this.shouldShowNewMsgButton = true;
        }
      }
      else
      {
        await this.scrollToLastMessage();
      }
    });
  }

  ngOnDestroy()
  {
    this.removeOldMessages();

    if (this.chatMessages.length > 0)
    {
      this.chatService.saveMessagesInStorage(
          this.chatMessages.reverse().map(message =>
              ({ ...message, hasViewed: true })
          ).slice(0, environment.maxCacheMessageCount),
          this.driverID
      );
    }

    if (this.chatMessageSubscription)
    {
      this.chatMessageSubscription.unsubscribe();
    }

    clearInterval(this.scrollToLastMessageInterval);

    // Reset values and close toggles
    this.showReplyOptions = false;
    this.showPredefinedMessages = false;
    this.showNoOlderMessages = false;
    this.hasScrolled = false;
    this.chatMessages = [];
    this.oldMessages = [];
    this.resetMessageValue();
  }

  removeOldMessages()
  {
    for (let i = this.chatMessages.length - 1; i >= 0; --i)
    {
      if (this.chatMessages[i].isOldMessage)
      {
        this.chatMessages.splice(i, 1);
      }
    }
  }

  setViewMessages()
  {
    this.chatMessageSubscription = this.sentMsgLastChatID.pipe(
      switchMap(() => {
        return this.chatService.getViewMessages(this.driverID, this.selectedContact)
        .pipe(
          map(messages => messages.map(val => ({...val, date: val.time.split('T')[0]}))
              .sort((a, b) => a.chatID - b.chatID)),
          delay(100)
        ) 
      })
    ).subscribe((messages: any[]) =>
    {
      this.showOldMessagesSpinner = false;
      this.isLoading = false;

      // This code handles if we got a response to a predefined message when doing an operator retrieve.
      // This happens if the last message sent is actually a predefined message (because the lastChatId is correct),
      // so it's not going to be returned in the get replies response.
      /* In case we got a response for a predefined message, it's going to be treated as a new message object
         (however with the same ChatID).
         So, we want to make sure we only want the object that has both the msg sent by the operator and the response of the driver.
         For that,we are comparing if there are objects with the same ID,
         and if there are, we are keeping the one that has a response field and removing the old one without a response.
      */
      for (let i = 0; i < messages.length; i++)
      {
        if ((messages[i]) &&
            (messages[i + 1]) &&
            (messages[i].chatID === messages[(i + 1)].chatID))
        {
          if ((messages[i].type === 3) &&
              (messages[i].response))
          {
            messages.splice((i + 1), 1);
          }
          else
          {
            messages.splice(i, 1);
            i--;
          }
        }
      }

      // This code handles if we have predefined messages/popups waiting for a response that are not the last message sent.
      // We want to do this everytime we do a check (which means each 5 seconds),
      // because there's no way to know when we are going to get a popup sent from Operator
      this.getReplies(messages);

      this.chatMessages = messages && messages.length ? messages : this.chatMessages;

      if (this.showOldMessages)
      {
        this.oldMessages.forEach(element => {
          element.isOldMessage = true;
          this.chatMessages.splice(0, 0, element);
        });
      }

      this.setDateWiseMessages();
      this.startTimer();
    },
    (error: TranslatableError) =>
    {
      this.chatService.handleError(error);
    });
  }

  getReplies(messages)
  {
    this.chatService.getReplies(this.driverID)
    .subscribe((response) =>
    {
      if (response.replies.length > 0)
      {
        response.replies.forEach(reply =>
        {
          let isPopupFromOperator = true;
          // Predefined message sent from the chat
          /* In case we got a response for a predefined message, it's going to be treated as a new message object
            (however with the same ChatID).
            So, we want to make sure we only want the object that has both the msg sent by the operator and the response of the driver.
            For that,we are comparing if there are objects with the same ID,
            and if there are, we are replacing the old data with the new one.
          */
          for (let message of messages)
          {
            if (reply.chatID === message.chatID)
            {
              message.response = reply.response;
              message.time = reply.time;
              message.type = ChatMsgType.withResponse;
              message.hasViewed = false;
              this.chatService.saveMessagesInStorage(messages, this.driverID);

              isPopupFromOperator = false;
              break;
            }
          }

          // Popup sent from Operator
          if (isPopupFromOperator)
          {
            let popupMessage = new ChatMessagesHasView();
            popupMessage.chatID = reply.chatID;
            popupMessage.response = reply.response;
            popupMessage.text = reply.text;
            popupMessage.operator = reply.operator;
            popupMessage.time = reply.time;
            popupMessage.type = ChatMsgType.withResponse;

            messages.push(popupMessage);
            this.chatService.saveMessagesInStorage(messages, this.driverID);
          }
        });

        this.setDateWiseMessages();
      }
    });
  }

  sendFromOperator(event?: any)
  {
    let drivers: number[] = [];
    let options;
    let type: ChatMsgType = ChatMsgType.fromOperator;

    if ((event) &&
        (event.keyCode !== 13))
    {
      return;
    }
    else
    {
      if ((this.message) &&
          (this.message.message))
      {
        // If the user is sending a predefined message or adding reply options, showReplyOptions = true
        // So, if true, change the type of the message to be send to 'message with options';
        if (this.showReplyOptions)
        {
          // Delete indexes that doesnt have a value, so we dont send option "null" to the server.
          for (let i = 0; i < this.message.options.length; i++)
          {
            let value = this.message.options[i];

            if ((!value) ||
                (value === null))
            {
              this.message.options.splice(i, 1);
              i--;
            }
          }

          // Make this message a predefined messages with options if, and only if, there are in fact options;
          // Otherwise (if the message has no reply options) this is still a simple message sent from operator.
          if (this.message.options.length > 0)
          {
            options = this.message.options;
            type = ChatMsgType.withOptions;
          }
        }

        drivers.push(this.driverID);

        this.chatService.chatMailService.operatorSend(this.userService.getJWT(),
                                                      type,
                                                      drivers,
                                                      this.message.message,
                                                      true,
                                                      options,
                                                      this.selectedContact.despatchGroupID)
        .subscribe(async sendResponse =>
        {
          this.resetMessageValue();

          this.chatMessages.map(val =>
              val.hasViewed = true
          );

          const sendMessage = new ChatMessagesWithDate();
          sendMessage.chatID = sendResponse.chatIDs[0];
          sendMessage.read = sendResponse.read;
          sendMessage.text = sendResponse.text;
          sendMessage.type = sendResponse.type;
          sendMessage.operator = sendResponse.operator;
          sendMessage.hasViewed = true;
          sendMessage.time = this.getToday();
          sendMessage.date = this.getToday().split('T')[0];

          if (sendResponse.options)
          {
            sendMessage.options = sendResponse.options;
          }

          this.chatMessages.push(sendMessage);

          this.chatService.saveMessagesInStorage(this.chatMessages, this.driverID);

          this.sentMsgLastChatID.next();
          
          this.setDateWiseMessages();
          await this.scrollToLastMessage();

          // Close toggles
          this.showReplyOptions = false;
          this.showPredefinedMessages = false;

          // The operator check only retrieves new data if there are unread messages in the database.
          // If the operator is the last person to send a message, we are adding this last message to recent chats (contacts list)
          // in order to make sure it is updated.
          let sentMessage = {message: sendMessage, toDriver: this.selectedContact}
          this.broadcaster.broadcast(BroadcasterEvent.EVENT_ON_MESSAGE_SENT_OR_RETRIEVED, sentMessage);
        },
        (error: TranslatableError) =>
        {
          this.chatService.handleError(error);
        });
      }
    }

  }

  messagesGroupBy(messages: any[], by: string)
  {
    const dateWiseChatMessages = [];
    const groupByObject = messages.reduce(
        (result, item) => ({
          ...result,
          [item[by]]: [
            ...(result[item[by]] || []),
            item,
          ],
        }),
        {},
    );
    // tslint:disable-next-line:forin
    for (const key in groupByObject)
    {
      dateWiseChatMessages.push({
        date: key,
        messages: groupByObject[key]
      });
    }
    return dateWiseChatMessages;
  }

  getToday()
  {
    return (new Date()).toISOString();
  }

  isMessageFromDriver(messageType)
  {
    return (messageType === ChatMsgType.fromDriver);
  }

  getDriverUsername()
  {
    if (this.selectedContact)
    {
      if (this.selectedContact instanceof DriverDetails)
      {
        return this.selectedContact.username;
      }

      return this.selectedContact.driverName;
    }
  }

  getOlderMessages()
  {
    this.showNoOlderMessages = false;
    this.showOldMessagesSpinner = true;

    if ((this.chatMessages) &&
        (this.chatMessages.length > 0))
    {
      let oldestChatID = this.chatMessages[0].chatID;

      this.chatService.chatMailService.operatorRetrieve(this.userService.getJWT(),
                                                                        oldestChatID,
                                                                        this.driverID,
                                                                        ChatCount.OLDER_MSGS,
                                                                        this.chatService.getDespatchGroups(),
                                                                        false,
                                                                        this.chatService.getOverrideUrl(SystemVerbs.SystemVerbOperatorRetrieve))
      .pipe(
        map(response => response.chat.map(val => ({...val, date: val.time.split('T')[0]}))),
        delay(100)
      )
      .subscribe(response =>
      {
        if (response.length > 0)
          {
            this.showOldMessages = true;
  
            response.forEach(element => {
              const oldMessage = new ChatMessagesWithDate();
              oldMessage.date = element.date;
              oldMessage.chatID = element.chatID;
              oldMessage.read = element.read;
              oldMessage.text = element.text;
              oldMessage.type = element.type;
              oldMessage.operator = element.operator;
              oldMessage.options = element.options;
              oldMessage.response = element.response;
              oldMessage.time = element.time;
              oldMessage.hasViewed = true;
  
              this.oldMessages.push(oldMessage);
              this.oldMessages.sort((a, b) => b.chatID - a.chatID);
              });
          }
          else
          {
            this.showNoOlderMessages = true;
          }
      });
    }
  }

  setDateWiseMessages()
  {
    this.chatMessagesDateWise = this.messagesGroupBy(
      this.chatMessages.filter(x => x.hasViewed)
      , 'date');
    this.newChatMessages = this.chatMessages.filter(x => !x.hasViewed);
  }

  getNumberNewChatMessages()
  {
    if ((this.newChatMessages) &&
        (this.newChatMessages.length === 1))
    {
      return this.translateService.instant('IDS_MESSAGE');
    }

    return this.translateService.instant('IDS_MESSAGES');
  }

  onChatScroll()
  {
    if (this.content)
    {
      // < 5 because we dont need to get to the very bottom of the page to hide the button.
      // If we are 5pixels away, the button will be hidden.
      if (((this.content.nativeElement.scrollHeight - this.content.nativeElement.scrollTop) - this.content.nativeElement.clientHeight) < 5)
      {
        this.shouldShowNewMsgButton = false;
        return true;
      }
      return false;
    }
    return false;
  }

  shouldShowNewMessagesBtn()
  {
    // If there arent any messages or if the view doesnt have enough messages in order to be scrollable - hide the button
    if ((this.isLoading) ||
        ((this.newChatMessages) &&
         (this.newChatMessages.length === 0)) ||
        ((this.content) &&
         (this.content.nativeElement.scrollHeight <= this.content.nativeElement.offsetHeight)))
    {
      return false;
    }

    return this.shouldShowNewMsgButton;
  }

  shouldShowNoOlderMessages()
  {
    return this.showNoOlderMessages;
  }

  shouldShowPredefinedMessages()
  {
    return this.showPredefinedMessages;
  }

  sendPredefinedMsg(msg)
  {
    this.showReplyOptions = true;
    this.showPredefinedMessages = false;

    this.resetMessageValue();

    // SelectedPredefinedMsgs were created in order to show the reply options on the screen, and allow the user to change them.
    this.selectedPredefinedMsg.message = msg.message;
    for (let i = 0; i < msg.options.length; i++)
    {
      this.selectedPredefinedMsg.options[i] = msg.options[i];
    }

    this.message.message = this.selectedPredefinedMsg.message;
    for (let i = 0; i < this.selectedPredefinedMsg.options.length; i++)
    {
      this.message.options[i] = this.selectedPredefinedMsg.options[i];
    }
  }

  predefinedMessagesToggle()
  {
    this.showPredefinedMessages = !this.showPredefinedMessages;
  }

  replyOptionsToggle()
  {
    this.showReplyOptions = !this.showReplyOptions;
    this.showPredefinedMessages = false;

    if (!this.showReplyOptions)
    {
      this.message.options = new Array(4);
      this.selectedPredefinedMsg.options = new Array(4);
    }
  }

  shouldShowReplyOptions()
  {
    return this.showReplyOptions;
  }

  resetMessageValue()
  {
    this.selectedPredefinedMsg = new PredefinedMessage();
    this.message = new PredefinedMessage();
  }

  async newMessageBtnAction()
  {
    await this.scrollToLastMessage();

    this.chatMessages.map(val =>
      val.hasViewed = true
    );

    this.chatService.saveMessagesInStorage(this.chatMessages, this.driverID);
    this.setDateWiseMessages();
  }
}
